import { useState, useEffect, useCallback, Fragment } from 'react';
import { entitiesApi, membersApi } from '../api/client';
import { Plus, Pencil, Trash2, X, Building2, AlertCircle, Search, ChevronDown, ChevronRight, Link2, User, Save } from 'lucide-react';
import OwnershipMap from '../components/common/OwnershipMap';

const ENTITY_TYPES = [
  { value: 'revocable_trust', label: 'Revocable Trust' },
  { value: 'irrevocable_trust', label: 'Irrevocable Trust' },
  { value: 'grat', label: 'GRAT' },
  { value: 'idgt', label: 'IDGT' },
  { value: 'ilit', label: 'ILIT' },
  { value: 'family_lp', label: 'Family LP' },
  { value: 'family_llc', label: 'Family LLC' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'donor_advised_fund', label: 'Donor Advised Fund' },
  { value: 'qtip_trust', label: 'QTIP Trust' },
  { value: 'bypass_trust', label: 'Bypass Trust' },
  { value: 'generation_skipping_trust', label: 'Gen-Skipping Trust' },
  { value: 'charitable_remainder_trust', label: 'Charitable Remainder' },
  { value: 'llc', label: 'LLC' },
  { value: 'other', label: 'Other' },
];

const TAX_FORM_OPTIONS: Record<string, { value: string; label: string }[]> = {
  llc: [
    { value: '1065', label: '1065 \u2013 Partnership' },
    { value: 'schedule_c', label: 'Disregarded Entity' },
    { value: '1120s', label: '1120-S \u2013 S Corp' },
    { value: '1120', label: '1120 \u2013 C Corp' },
  ],
  family_llc: [
    { value: '1065', label: '1065 \u2013 Partnership' },
    { value: 'schedule_c', label: 'Schedule C \u2013 Disregarded' },
    { value: '1120s', label: '1120-S \u2013 S Corp' },
    { value: '1120', label: '1120 \u2013 C Corp' },
  ],
  family_lp: [{ value: '1065', label: '1065 \u2013 Partnership' }],
  s_corp: [{ value: '1120s', label: '1120-S \u2013 S Corp' }],
  c_corp: [{ value: '1120', label: '1120 \u2013 C Corp' }],
  revocable_trust: [{ value: '1040_grantor', label: '1040 \u2013 Grantor (Disregarded)' }],
  irrevocable_trust: [
    { value: '1041', label: '1041 \u2013 Non-Grantor Trust' },
    { value: '1040_grantor', label: '1040 \u2013 Grantor (Disregarded)' },
  ],
  grat: [
    { value: '1041', label: '1041 \u2013 Non-Grantor Trust' },
    { value: '1040_grantor', label: '1040 \u2013 Grantor (Disregarded)' },
  ],
  idgt: [{ value: '1040_grantor', label: '1040 \u2013 Grantor (Disregarded)' }],
  ilit: [
    { value: '1041', label: '1041 \u2013 Non-Grantor Trust' },
    { value: '1040_grantor', label: '1040 \u2013 Grantor (Disregarded)' },
  ],
  foundation: [{ value: '990pf', label: '990-PF \u2013 Foundation' }],
  donor_advised_fund: [{ value: 'none', label: 'None (Sponsor Files)' }],
  qtip_trust: [{ value: '1041', label: '1041 \u2013 Non-Grantor Trust' }],
  bypass_trust: [{ value: '1041', label: '1041 \u2013 Non-Grantor Trust' }],
  generation_skipping_trust: [{ value: '1041', label: '1041 \u2013 Non-Grantor Trust' }],
  charitable_remainder_trust: [{ value: '5227', label: '5227 \u2013 CRT' }],
  other: [
    { value: '1065', label: '1065 \u2013 Partnership' },
    { value: '1120s', label: '1120-S \u2013 S Corp' },
    { value: '1120', label: '1120 \u2013 C Corp' },
    { value: '1041', label: '1041 \u2013 Trust/Estate' },
    { value: '1040_grantor', label: '1040 \u2013 Grantor' },
    { value: 'none', label: 'None / Other' },
  ],
};

const DEFAULT_TAX_FORM: Record<string, string> = {
  llc: '1065', family_llc: '1065', family_lp: '1065',
  revocable_trust: '1040_grantor', irrevocable_trust: '1041', grat: '1041',
  idgt: '1040_grantor', ilit: '1041', foundation: '990pf',
  donor_advised_fund: 'none', qtip_trust: '1041', bypass_trust: '1041',
  generation_skipping_trust: '1041', charitable_remainder_trust: '5227', other: 'none',
};

const TAX_FORM_LABELS: Record<string, string> = {
  schedule_c: 'Disregarded Entity',
  '1120s': '1120-S \u2013 S Corp', '1120': '1120 \u2013 C Corp',
  '1041': '1041 \u2013 Trust/Estate', '1040_grantor': '1040 \u2013 Grantor',
  '990pf': '990-PF \u2013 Foundation', '5227': '5227 \u2013 CRT', none: 'N/A',
};

const INTEREST_CLASSES = [
  { value: 'common', label: 'Common / Member' },
  { value: 'GP', label: 'General Partner (GP)' },
  { value: 'LP', label: 'Limited Partner (LP)' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'beneficiary', label: 'Beneficiary' },
  { value: 'managing_member', label: 'Managing Member' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

function typeLabel(val: string): string {
  return ENTITY_TYPES.find(t => t.value === val)?.label || val;
}
function taxFormLabel(val: string | null | undefined): string {
  if (!val) return '\u2014';
  return TAX_FORM_LABELS[val] || val;
}
function fmtCurrency(val: number | string | null | undefined): string {
  if (val == null) return '$0';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

interface Entity { id: string; name: string; entity_type: string; tax_form?: string; ein?: string; formation_date?: string; domicile_state?: string; grantor_id?: string; is_grantor_trust?: boolean; is_operating_business?: boolean; is_closely_held?: boolean; section_6166_eligible?: boolean; notes?: string; total_assets?: number | string; grantor_name?: string; }
interface OwnershipStake { id: string; entity_id: string; member_id?: string; owner_entity_id?: string; owner_name: string; interest_class: string; ownership_pct: number | string; capital_account: number | string; is_voting: boolean; effective_date?: string; notes?: string; member_name?: string; owner_entity_name?: string; }
interface Member { id: string; first_name: string; last_name: string; }
interface FormData { name: string; entityType: string; taxForm: string; ein: string; formationDate: string; domicileState: string; isGrantorTrust: boolean; isOperatingBusiness: boolean; isCloselyHeld: boolean; section6166Eligible: boolean; notes: string; }

const emptyForm: FormData = {
  name: '', entityType: 'family_llc', taxForm: '1065', ein: '', formationDate: '',
  domicileState: '', isGrantorTrust: false, isOperatingBusiness: false,
  isCloselyHeld: false, section6166Eligible: false, notes: '',
};

function TypeBadge({ type }: { type: string }) {
  const trustTypes = ['revocable_trust','irrevocable_trust','grat','idgt','ilit','qtip_trust','bypass_trust','generation_skipping_trust','charitable_remainder_trust'];
  const corpTypes = ['s_corp','c_corp'];
  const partnerTypes = ['family_lp','family_llc'];
  let classes = 'badge-navy';
  if (trustTypes.includes(type)) classes = 'badge-burgundy';
  else if (corpTypes.includes(type)) classes = 'badge-hunter';
  else if (partnerTypes.includes(type)) classes = 'badge-gold';
  return <span className={classes}>{typeLabel(type)}</span>;
}

function TaxFormBadge({ form }: { form: string | null | undefined }) {
  if (!form || form === 'none') return <span className="text-xs text-navy-300 font-mono">{'\u2014'}</span>;
  const passThrough = ['1065', '1120s', 'schedule_c', '1040_grantor'];
  const cls = passThrough.includes(form)
    ? 'inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-emerald-50 text-emerald-700 border border-emerald-200'
    : 'inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-slate-50 text-slate-600 border border-slate-200';
  return <span className={cls}>{taxFormLabel(form)}</span>;
}

function OwnershipRow({ stake, members, entities, entityId, onSaved, onDeleted }: {
  stake: OwnershipStake; members: Member[]; entities: Entity[]; entityId: string;
  onSaved: (updated: OwnershipStake) => void; onDeleted: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [ownerType, setOwnerType] = useState<'member' | 'entity'>(stake.owner_entity_id ? 'entity' : 'member');
  const [local, setLocal] = useState({
    memberId: stake.member_id || '', ownerEntityId: stake.owner_entity_id || '',
    ownerName: stake.owner_name || '', interestClass: stake.interest_class || 'common',
    ownershipPct: parseFloat(String(stake.ownership_pct)) || 0,
    capitalAccount: parseFloat(String(stake.capital_account)) || 0,
    isVoting: stake.is_voting ?? true,
  });
  function handleOwnerChange(value: string) {
    if (ownerType === 'member') {
      const m = members.find(m => m.id === value);
      setLocal({ ...local, memberId: value, ownerEntityId: '', ownerName: m ? `${m.first_name} ${m.last_name}` : '' });
    } else {
      const e = entities.find(e => e.id === value);
      setLocal({ ...local, ownerEntityId: value, memberId: '', ownerName: e?.name || '' });
    }
  }
  async function save() {
    try {
      setSaving(true);
      const payload = {
        memberId: ownerType === 'member' ? local.memberId || undefined : undefined,
        ownerEntityId: ownerType === 'entity' ? local.ownerEntityId || undefined : undefined,
        ownerName: local.ownerName, interestClass: local.interestClass,
        ownershipPct: local.ownershipPct, capitalAccount: local.capitalAccount, isVoting: local.isVoting,
      };
      const res = await entitiesApi.updateOwnership(entityId, stake.id, payload);
      onSaved(res.data.data); setEditing(false);
    } catch (err: any) { alert(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  }
  async function remove() {
    if (!confirm('Remove this ownership stake?')) return;
    try { setDeleting(true); await entitiesApi.deleteOwnership(entityId, stake.id); onDeleted(stake.id); }
    catch (err: any) { alert(err.response?.data?.error || 'Failed to delete'); }
    finally { setDeleting(false); }
  }
  const displayName = stake.member_name || stake.owner_entity_name || stake.owner_name || '\u2014';
  const isEntity = !!stake.owner_entity_id;
  const pct = parseFloat(String(stake.ownership_pct));
  if (!editing) {
    return (
      <tr className="border-b border-ivory-200 hover:bg-ivory-50 transition-colors">
        <td className="px-4 py-2.5"><div className="flex items-center gap-1.5">
          {isEntity ? <Link2 size={12} className="text-navy-400 flex-shrink-0" /> : <User size={12} className="text-navy-400 flex-shrink-0" />}
          <span className="text-sm text-navy-700 font-sans">{displayName}</span>
        </div></td>
        <td className="px-4 py-2.5 text-xs text-navy-500 font-sans">{stake.interest_class?.toUpperCase() || 'COMMON'}</td>
        <td className="px-4 py-2.5 text-right font-mono text-sm text-navy-700">{pct.toFixed(1)}%</td>
        <td className="px-4 py-2.5 text-right font-mono text-sm text-navy-600">{fmtCurrency(stake.capital_account)}</td>
        <td className="px-4 py-2.5 text-center">{stake.is_voting ? <span className="text-xs text-emerald-600">Yes</span> : <span className="text-xs text-navy-300">No</span>}</td>
        <td className="px-4 py-2.5 text-right"><div className="flex items-center justify-end gap-1">
          <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-ivory-200 text-navy-400 hover:text-navy-700" title="Edit"><Pencil size={12} /></button>
          <button onClick={remove} disabled={deleting} className="p-1 rounded hover:bg-red-50 text-navy-400 hover:text-red-600" title="Remove"><Trash2 size={12} /></button>
        </div></td>
      </tr>
    );
  }
  return (
    <tr className="border-b border-navy-200 bg-navy-50/30">
      <td className="px-3 py-2"><div className="space-y-1"><div className="flex gap-1">
        <button onClick={() => { setOwnerType('member'); setLocal({ ...local, ownerEntityId: '' }); }} className={`px-2 py-0.5 text-xs rounded ${ownerType === 'member' ? 'bg-navy-700 text-white' : 'bg-ivory-200 text-navy-500'}`}>Person</button>
        <button onClick={() => { setOwnerType('entity'); setLocal({ ...local, memberId: '' }); }} className={`px-2 py-0.5 text-xs rounded ${ownerType === 'entity' ? 'bg-navy-700 text-white' : 'bg-ivory-200 text-navy-500'}`}>Entity</button>
      </div>
      {ownerType === 'member' ? (
        <select className="input text-xs py-1" value={local.memberId} onChange={e => handleOwnerChange(e.target.value)}>
          <option value="">Select member...</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
        </select>
      ) : (
        <select className="input text-xs py-1" value={local.ownerEntityId} onChange={e => handleOwnerChange(e.target.value)}>
          <option value="">Select entity...</option>
          {entities.filter(e => e.id !== entityId).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      )}</div></td>
      <td className="px-3 py-2"><select className="input text-xs py-1" value={local.interestClass} onChange={e => setLocal({ ...local, interestClass: e.target.value })}>
        {INTEREST_CLASSES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select></td>
      <td className="px-3 py-2"><input type="number" step="0.1" min="0" max="100" className="input text-xs py-1 text-right font-mono w-20" value={local.ownershipPct} onChange={e => setLocal({ ...local, ownershipPct: parseFloat(e.target.value) || 0 })} /></td>
      <td className="px-3 py-2"><input type="number" step="1000" min="0" className="input text-xs py-1 text-right font-mono w-24" value={local.capitalAccount || ''} onChange={e => setLocal({ ...local, capitalAccount: parseFloat(e.target.value) || 0 })} /></td>
      <td className="px-3 py-2 text-center"><input type="checkbox" checked={local.isVoting} onChange={e => setLocal({ ...local, isVoting: e.target.checked })} className="w-3.5 h-3.5 rounded border-ivory-400 text-navy-600" /></td>
      <td className="px-3 py-2 text-right"><div className="flex items-center justify-end gap-1">
        <button onClick={save} disabled={saving} className="p-1 rounded bg-navy-700 text-white hover:bg-navy-600" title="Save"><Save size={12} /></button>
        <button onClick={() => setEditing(false)} className="p-1 rounded hover:bg-ivory-200 text-navy-400" title="Cancel"><X size={12} /></button>
      </div></td>
    </tr>
  );
}

function AddOwnershipRow({ entityId, members, entities, onAdded }: {
  entityId: string; members: Member[]; entities: Entity[]; onAdded: (stake: OwnershipStake) => void;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ownerType, setOwnerType] = useState<'member' | 'entity'>('member');
  const [form, setForm] = useState({ memberId: '', ownerEntityId: '', ownerName: '', interestClass: 'common', ownershipPct: 0, capitalAccount: 0, isVoting: true });
  function handleOwnerChange(value: string) {
    if (ownerType === 'member') {
      const m = members.find(m => m.id === value);
      setForm({ ...form, memberId: value, ownerEntityId: '', ownerName: m ? `${m.first_name} ${m.last_name}` : '' });
    } else {
      const e = entities.find(e => e.id === value);
      setForm({ ...form, ownerEntityId: value, memberId: '', ownerName: e?.name || '' });
    }
  }
  async function save() {
    if (!form.ownerName) { alert('Select an owner'); return; }
    try {
      setSaving(true);
      const payload = {
        memberId: ownerType === 'member' ? form.memberId || undefined : undefined,
        ownerEntityId: ownerType === 'entity' ? form.ownerEntityId || undefined : undefined,
        ownerName: form.ownerName, interestClass: form.interestClass,
        ownershipPct: form.ownershipPct, capitalAccount: form.capitalAccount, isVoting: form.isVoting,
      };
      const res = await entitiesApi.addOwnership(entityId, payload);
      onAdded(res.data.data);
      setForm({ memberId: '', ownerEntityId: '', ownerName: '', interestClass: 'common', ownershipPct: 0, capitalAccount: 0, isVoting: true });
      setOpen(false);
    } catch (err: any) { alert(err.response?.data?.error || 'Failed to add'); }
    finally { setSaving(false); }
  }
  if (!open) return (<tr><td colSpan={6} className="px-4 py-2"><button onClick={() => setOpen(true)} className="text-xs font-sans font-bold text-navy-600 hover:text-navy-800 flex items-center gap-1"><Plus size={12} /> Add Owner</button></td></tr>);
  return (
    <tr className="border-b border-navy-200 bg-emerald-50/20">
      <td className="px-3 py-2"><div className="space-y-1"><div className="flex gap-1">
        <button onClick={() => { setOwnerType('member'); setForm({ ...form, ownerEntityId: '' }); }} className={`px-2 py-0.5 text-xs rounded ${ownerType === 'member' ? 'bg-navy-700 text-white' : 'bg-ivory-200 text-navy-500'}`}>Person</button>
        <button onClick={() => { setOwnerType('entity'); setForm({ ...form, memberId: '' }); }} className={`px-2 py-0.5 text-xs rounded ${ownerType === 'entity' ? 'bg-navy-700 text-white' : 'bg-ivory-200 text-navy-500'}`}>Entity</button>
      </div>
      {ownerType === 'member' ? (
        <select className="input text-xs py-1" value={form.memberId} onChange={e => handleOwnerChange(e.target.value)}>
          <option value="">Select member...</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
        </select>
      ) : (
        <select className="input text-xs py-1" value={form.ownerEntityId} onChange={e => handleOwnerChange(e.target.value)}>
          <option value="">Select entity...</option>
          {entities.filter(e => e.id !== entityId).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      )}</div></td>
      <td className="px-3 py-2"><select className="input text-xs py-1" value={form.interestClass} onChange={e => setForm({ ...form, interestClass: e.target.value })}>
        {INTEREST_CLASSES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select></td>
      <td className="px-3 py-2"><input type="number" step="0.1" min="0" max="100" className="input text-xs py-1 text-right font-mono w-20" value={form.ownershipPct} onChange={e => setForm({ ...form, ownershipPct: parseFloat(e.target.value) || 0 })} /></td>
      <td className="px-3 py-2"><input type="number" step="1000" min="0" className="input text-xs py-1 text-right font-mono w-24" value={form.capitalAccount || ''} onChange={e => setForm({ ...form, capitalAccount: parseFloat(e.target.value) || 0 })} /></td>
      <td className="px-3 py-2 text-center"><input type="checkbox" checked={form.isVoting} onChange={e => setForm({ ...form, isVoting: e.target.checked })} className="w-3.5 h-3.5 rounded border-ivory-400 text-navy-600" /></td>
      <td className="px-3 py-2 text-right"><div className="flex items-center justify-end gap-1">
        <button onClick={save} disabled={saving} className="p-1 rounded bg-emerald-600 text-white hover:bg-emerald-500" title="Add"><Save size={12} /></button>
        <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-ivory-200 text-navy-400" title="Cancel"><X size={12} /></button>
      </div></td>
    </tr>
  );
}

function OwnershipSection({ entityId, members, entities }: { entityId: string; members: Member[]; entities: Entity[]; }) {
  const [stakes, setStakes] = useState<OwnershipStake[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { setLoading(true); const res = await entitiesApi.listOwnership(entityId); setStakes(res.data.data || []); } catch {} finally { setLoading(false); } })(); }, [entityId]);
  const totalPct = stakes.reduce((sum, s) => sum + parseFloat(String(s.ownership_pct) || '0'), 0);
  const pctWarning = totalPct > 0 && Math.abs(totalPct - 100) > 0.01;
  function handleSaved(updated: OwnershipStake) { setStakes(prev => prev.map(s => s.id === updated.id ? updated : s)); }
  function handleDeleted(id: string) { setStakes(prev => prev.filter(s => s.id !== id)); }
  function handleAdded(newStake: OwnershipStake) { setStakes(prev => [...prev, newStake]); }
  if (loading) return <div className="px-6 py-4 text-xs text-navy-400 animate-pulse">Loading ownership...</div>;
  return (
    <div className="px-6 pb-4">
      <div className="rounded border border-ivory-300 overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-ivory-200 border-b border-ivory-300">
            <th className="text-left px-4 py-2 text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider">Owner</th>
            <th className="text-left px-4 py-2 text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider">Class</th>
            <th className="text-right px-4 py-2 text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider">%</th>
            <th className="text-right px-4 py-2 text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider">Capital Acct</th>
            <th className="text-center px-4 py-2 text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider">Vote</th>
            <th className="text-right px-4 py-2 text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider w-20"></th>
          </tr></thead>
          <tbody>
            {stakes.length === 0 && <tr><td colSpan={6} className="px-4 py-3 text-xs text-navy-400 text-center italic">No owners assigned yet</td></tr>}
            {stakes.map(s => <OwnershipRow key={s.id} stake={s} members={members} entities={entities} entityId={entityId} onSaved={handleSaved} onDeleted={handleDeleted} />)}
            <AddOwnershipRow entityId={entityId} members={members} entities={entities} onAdded={handleAdded} />
          </tbody>
        </table>
        <div className={`flex items-center justify-between px-4 py-2 border-t ${pctWarning ? 'bg-amber-50 border-amber-200' : 'bg-ivory-100 border-ivory-300'}`}>
          <span className="text-xs font-sans font-semibold text-navy-500">Total Ownership</span>
          <span className={`text-sm font-mono font-bold ${pctWarning ? 'text-amber-700' : 'text-navy-700'}`}>
            {totalPct.toFixed(1)}%
            {pctWarning && <span className="text-xs font-sans font-normal ml-2 text-amber-600">
              ({totalPct < 100 ? `${(100 - totalPct).toFixed(1)}% unassigned` : `${(totalPct - 100).toFixed(1)}% over-allocated`})
            </span>}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function EntitiesPage() {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [view, setView] = useState<'map' | 'table'>('map');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchEntities = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };
      if (filterType) params.type = filterType;
      const [entRes, memRes] = await Promise.all([entitiesApi.list(params), membersApi.list({ limit: 100 })]);
      setEntities(entRes.data.data || []); setMembers(memRes.data.data || []); setError('');
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to load entities'); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  const filtered = entities.filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase()));

  function handleEntityTypeChange(newType: string) {
    const defaultForm = DEFAULT_TAX_FORM[newType] || 'none';
    setForm({ ...form, entityType: newType, taxForm: defaultForm });
  }
  function openCreate() { setEditingId(null); setForm(emptyForm); setFormError(''); setShowModal(true); }
  function openEdit(entity: Entity) {
    setEditingId(entity.id);
    setForm({
      name: entity.name, entityType: entity.entity_type,
      taxForm: entity.tax_form || DEFAULT_TAX_FORM[entity.entity_type] || 'none',
      ein: entity.ein || '', formationDate: entity.formation_date || '',
      domicileState: entity.domicile_state || '', isGrantorTrust: entity.is_grantor_trust || false,
      isOperatingBusiness: entity.is_operating_business || false, isCloselyHeld: entity.is_closely_held || false,
      section6166Eligible: entity.section_6166_eligible || false, notes: entity.notes || '',
    });
    setFormError(''); setShowModal(true);
  }
  async function handleSave() {
    if (!form.name.trim()) { setFormError('Entity name is required'); return; }
    try {
      setSaving(true); setFormError('');
      const payload = {
        name: form.name.trim(), entityType: form.entityType, taxForm: form.taxForm || undefined,
        ein: form.ein || undefined, formationDate: form.formationDate || undefined,
        domicileState: form.domicileState || undefined, isGrantorTrust: form.isGrantorTrust,
        isOperatingBusiness: form.isOperatingBusiness, isCloselyHeld: form.isCloselyHeld,
        section6166Eligible: form.section6166Eligible, notes: form.notes || undefined,
      };
      if (editingId) await entitiesApi.update(editingId, payload);
      else await entitiesApi.create(payload);
      setShowModal(false); fetchEntities();
    } catch (err: any) { setFormError(err.response?.data?.error || 'Failed to save entity'); }
    finally { setSaving(false); }
  }
  async function handleDelete() {
    if (!deleteId) return;
    try { setDeleting(true); await entitiesApi.delete(deleteId); setDeleteId(null); fetchEntities(); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to delete'); setDeleteId(null); }
    finally { setDeleting(false); }
  }

  const availableTaxForms = TAX_FORM_OPTIONS[form.entityType] || TAX_FORM_OPTIONS.other;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-navy-900">Entities & Ownership</h1>
          <p className="text-sm text-navy-400 font-sans mt-1">Trusts, LLCs, partnerships, and corporate entities in the family structure</p>
        </div>
        <button onClick={openCreate} className="btn-primary"><Plus size={16} className="mr-2" />Add Entity</button>
      </div>
      <div className="card mb-6"><div className="flex items-center gap-4">
        <div style={{ display: 'flex', gap: 4, background: '#EBE3D6', borderRadius: 3, padding: 3 }}>
          {(['map', 'table'] as const).map(v => (
            <button key={v} onClick={() => setView(v)} style={{ padding: '6px 14px', borderRadius: 2, border: 'none', cursor: 'pointer', fontFamily: "'Lato', sans-serif", fontSize: 11, fontWeight: 600, background: view === v ? '#1B2A4A' : 'transparent', color: view === v ? '#FAF7F2' : '#7A7265', transition: 'all 0.15s' }}>{v === 'map' ? 'Structure' : 'Registry'}</button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" />
          <input type="text" placeholder="Search entities..." value={search} onChange={(e) => setSearch(e.target.value)} className="input pl-9" />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="input w-auto">
          <option value="">All Types</option>
          {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div className="ml-auto text-sm text-navy-400 font-sans">{filtered.length} {filtered.length === 1 ? 'entity' : 'entities'}</div>
      </div></div>
      {error && <div className="flex items-center gap-2 p-4 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={16} />{error}</div>}
      {view === 'map' && <OwnershipMap />}
      {view === 'table' && (
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><p className="text-sm text-navy-400 font-sans animate-pulse">Loading entities...</p></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center"><Building2 size={32} className="mx-auto mb-3 text-navy-200" />
            <p className="text-sm text-navy-500 font-sans font-medium">No entities yet</p>
            <p className="text-xs text-navy-400 font-sans mt-1">Add your first entity to get started</p></div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full"><thead>
            <tr className="border-b border-ivory-300 bg-ivory-200">
              <th className="table-header text-left px-3 py-3 w-8"></th>
              <th className="table-header text-left px-4 py-3">Name</th>
              <th className="table-header text-left px-4 py-3">Type</th>
              <th className="table-header text-left px-4 py-3">Tax Form</th>
              <th className="table-header text-left px-4 py-3">State</th>
              <th className="table-header text-center px-4 py-3">Grantor</th>
              <th className="table-header text-right px-4 py-3">Total Assets</th>
              <th className="table-header text-right px-6 py-3 w-24">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-ivory-200">
              {filtered.map((entity) => {
                const isExpanded = expandedId === entity.id;
                return (
                  <Fragment key={entity.id}>
                    <tr className={`transition-colors cursor-pointer ${isExpanded ? 'bg-ivory-100' : 'hover:bg-ivory-100'}`}
                      onClick={() => setExpandedId(isExpanded ? null : entity.id)}>
                      <td className="px-3 py-3.5 text-center">
                        {isExpanded ? <ChevronDown size={14} className="text-navy-500 mx-auto" /> : <ChevronRight size={14} className="text-navy-400 mx-auto" />}
                      </td>
                      <td className="px-4 py-3.5"><span className="font-sans text-sm font-semibold text-navy-800">{entity.name}</span>
                        {entity.notes && <p className="text-xs text-navy-400 mt-0.5 truncate max-w-xs">{entity.notes}</p>}</td>
                      <td className="px-4 py-3.5"><TypeBadge type={entity.entity_type} /></td>
                      <td className="px-4 py-3.5"><TaxFormBadge form={entity.tax_form} /></td>
                      <td className="px-4 py-3.5 text-sm text-navy-600 font-sans">{entity.domicile_state || '\u2014'}</td>
                      <td className="px-4 py-3.5 text-center">{entity.is_grantor_trust ? <span className="badge-hunter">Yes</span> : <span className="text-xs text-navy-300">{'\u2014'}</span>}</td>
                      <td className="px-4 py-3.5 text-right font-mono text-sm text-navy-700 font-medium">{fmtCurrency(entity.total_assets)}</td>
                      <td className="px-6 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(entity)} className="p-1.5 rounded hover:bg-ivory-200 text-navy-400 hover:text-navy-700 transition-colors" title="Edit"><Pencil size={14} /></button>
                          <button onClick={() => setDeleteId(entity.id)} className="p-1.5 rounded hover:bg-red-50 text-navy-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && <tr><td colSpan={8} className="p-0 bg-ivory-50 border-b-2 border-navy-200">
                      <OwnershipSection entityId={entity.id} members={members} entities={entities} />
                    </td></tr>}
                  </Fragment>
                );
              })}
            </tbody></table></div>
        )}
      </div>)}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-ivory-300 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ivory-300">
              <h3 className="font-display text-lg text-navy-900">{editingId ? 'Edit Entity' : 'Add Entity'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded hover:bg-ivory-200 text-navy-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
              {formError && <div className="flex items-center gap-2 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} />{formError}</div>}
              <div><label className="input-label">Entity Name *</label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Smith Family Holdings, LP" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="input-label">Entity Type *</label>
                  <select className="input" value={form.entityType} onChange={(e) => handleEntityTypeChange(e.target.value)}>
                    {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select></div>
                <div><label className="input-label">Tax Form *</label>
                  <select className="input" value={form.taxForm} onChange={(e) => setForm({ ...form, taxForm: e.target.value })}>
                    {availableTaxForms.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                  {availableTaxForms.length === 1 && <p className="text-xs text-navy-400 font-sans mt-1 italic">Only filing option for this entity type</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="input-label">EIN</label>
                  <input type="text" className="input" value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} placeholder="XX-XXXXXXX" /></div>
                <div><label className="input-label">Domicile State</label>
                  <select className="input" value={form.domicileState} onChange={(e) => setForm({ ...form, domicileState: e.target.value })}>
                    <option value="">Select state</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select></div>
              </div>
              <div><label className="input-label">Formation Date</label>
                <input type="date" className="input" value={form.formationDate} onChange={(e) => setForm({ ...form, formationDate: e.target.value })} /></div>
              <div className="flex items-center gap-3 py-1">
                <input type="checkbox" id="isGrantorTrust" checked={form.isGrantorTrust} onChange={(e) => setForm({ ...form, isGrantorTrust: e.target.checked })} className="w-4 h-4 rounded border-ivory-400 text-navy-600 focus:ring-navy-300" />
                <label htmlFor="isGrantorTrust" className="text-sm text-navy-700 font-sans">Grantor trust (income taxed to grantor)</label>
              </div>
              <div className="border-t border-ivory-300 pt-4 mt-2">
                <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-3">{'\u00A7'}6166 Eligibility</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 text-sm text-navy-700 font-sans">
                    <input type="checkbox" checked={form.isOperatingBusiness} onChange={(e) => setForm({ ...form, isOperatingBusiness: e.target.checked })} className="w-4 h-4 rounded border-ivory-400 text-navy-600 focus:ring-navy-300" />
                    Active trade or business (not passive holding)</label>
                  <label className="flex items-center gap-3 text-sm text-navy-700 font-sans">
                    <input type="checkbox" checked={form.isCloselyHeld} onChange={(e) => setForm({ ...form, isCloselyHeld: e.target.checked })} className="w-4 h-4 rounded border-ivory-400 text-navy-600 focus:ring-navy-300" />
                    Closely held ({'\u2264'}45 owners or decedent owns {'\u2265'}20%)</label>
                  <label className="flex items-center gap-3 text-sm text-navy-700 font-sans">
                    <input type="checkbox" checked={form.section6166Eligible} onChange={(e) => setForm({ ...form, section6166Eligible: e.target.checked })} className="w-4 h-4 rounded border-ivory-400 text-navy-600 focus:ring-navy-300" />
                    Confirmed {'\u00A7'}6166 eligible (advisor override)</label>
                </div>
                <p className="text-xs text-navy-400 font-sans mt-2 italic">{'\u00A7'}6166 allows estate tax installment payments over 14 years for qualifying closely held businesses exceeding 35% of the adjusted gross estate.</p>
              </div>
              <div><label className="input-label">Notes</label>
                <textarea className="input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes about this entity..." /></div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-ivory-300 bg-ivory-100 rounded-b-lg">
              <button onClick={() => setShowModal(false)} className="btn-secondary" disabled={saving}>Cancel</button>
              <button onClick={handleSave} className="btn-primary" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update Entity' : 'Create Entity'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-ivory-300 w-full max-w-sm mx-4 p-6">
            <h3 className="font-display text-lg text-navy-900 mb-2">Delete Entity</h3>
            <p className="text-sm text-navy-500 font-sans mb-6">Are you sure? This will remove the entity and unlink any associated assets and liabilities. This action cannot be undone.</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="btn-secondary" disabled={deleting}>Cancel</button>
              <button onClick={handleDelete} className="btn-burgundy" disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
